/**
 * Billing Page - Displays subscription plan, usage, and transaction history.
 */

import {
  Box,
  Button,
  Group,
  LoadingOverlay,
  Paper,
  Stack,
  Text,
} from "@mantine/core";
import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { useAuthStore } from "@utils/stores/authStore";
import { useBalanceHistory } from "@pages/Credits/hooks/useBalanceHistory";
import { useMembers } from "@/utils/hooks/useMembers";
import { TransactionHistorySection } from "@/components/Team/TeamManager/components/TransactionHistorySection";
import {
  getBillingInformation,
  loadStripePricingTableScript,
  getBankStatus,
  updateBankStatus,
} from "./api";
import {
  PlanCard,
  PlanCardSkeleton,
  CancelSubscriptionModal,
  PricingTableModal,
  BillingErrorState,
  PayByPrepFeesCard,
  PurchaseCreditsBundleModal,
  PurchaseCommercialAddOnModal,
} from "./components";
import type {
  BillingProps,
  PricingTableData,
  CancelSubscriptionResponse,
  PayByPrepFeesInfo,
} from "./types";
import type { BillingPortalInfo } from "@/utils/billing/types";
import { extractApiError, showError } from "@/utils/errors";
import "./Billing.scss";

const Billing = ({ loading }: BillingProps) => {
  const { role, shouldHideBillingSettings } = useAuthStore();
  const hideBilling = shouldHideBillingSettings();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  const [loadingBilling, setLoadingBilling] = useState(false);
  const [cancelModalOpened, setCancelModalOpened] = useState(false);
  const [pricingModalOpened, setPricingModalOpened] = useState(false);
  const [loadingBillingInfo, setLoadingBillingInfo] = useState(false);
  const [billingData, setBillingData] = useState<BillingPortalInfo | null>(
    null
  );
  const [pricingTableData, setPricingTableData] =
    useState<PricingTableData | null>(null);
  const [billingError, setBillingError] = useState<string | null>(null);
  const [actionHandled, setActionHandled] = useState(false);
  const [prepFeesInfo, setPrepFeesInfo] = useState<PayByPrepFeesInfo | null>(
    null
  );
  const [loadingPrepFees, setLoadingPrepFees] = useState(false);
  const [purchaseCreditsModalOpened, setPurchaseCreditsModalOpened] =
    useState(false);
  const [commercialAddOnModalOpened, setCommercialAddOnModalOpened] =
    useState(false);

  // Fetch transaction history for TransactionHistorySection
  const { data: transactionHistory, isLoading: isHistoryLoading } =
    useBalanceHistory(1, 1000, role);

  // Fetch team members for TransactionHistorySection
  const { membersList, loading: isMembersLoading } = useMembers(
    false,
    role || null
  );

  /**
   * Load billing information on component mount.
   */
  const loadBillingInformation = async () => {
    setLoadingBillingInfo(true);
    setBillingError(null);
    try {
      const response = await getBillingInformation(undefined, undefined, true);
      setBillingData(response);
    } catch (error) {
      console.error("Error loading billing information:", error);
      setBillingError("Failed to load billing information. Please try again.");
    } finally {
      setLoadingBillingInfo(false);
    }
  };

  /**
   * Retry loading billing information when there's an error.
   */
  const retryLoadBilling = async () => {
    await loadBillingInformation();
  };

  /**
   * Load billing information on component mount.
   */
  useEffect(() => {
    if (hideBilling) {
      return;
    }
    loadBillingInformation();
  }, [hideBilling]);

  /**
   * Load Pay by Prep Fees info when user is on pay_by_refund plan.
   */
  useEffect(() => {
    const signupSource = billingData?.currentPlan?.signupSource;
    if (signupSource === "pay_by_refund") {
      const loadBankStatus = async () => {
        setLoadingPrepFees(true);
        try {
          const data = await getBankStatus();
          setPrepFeesInfo(data);
        } catch (error) {
          console.error("Error loading bank status:", error);
          // Set to null on error so the component can show an error state
          setPrepFeesInfo(null);
        } finally {
          setLoadingPrepFees(false);
        }
      };
      loadBankStatus();
    }
  }, [billingData?.currentPlan?.signupSource]);

  /**
   * Load Stripe pricing table script.
   */
  useEffect(() => {
    loadStripePricingTableScript();
  }, []);

  /**
   * Handle URL action param to auto-open modals.
   * Supports action=cancel (opens CancelSubscriptionModal).
   * Clears the action param after handling to prevent re-triggering on refresh.
   */
  useEffect(() => {
    // Only handle action once billing data is loaded and we haven't already handled it
    if (!billingData || actionHandled || loadingBillingInfo) return;

    const action = searchParams.get("action");
    if (!action) return;

    // Handle the action
    if (action === "cancel") {
      const currentPlan = billingData.currentPlan;
      if (currentPlan?.canCancelSubscription === true) {
        setCancelModalOpened(true);
      }
    }

    // Clear the action param from URL to prevent re-triggering
    const newParams = new URLSearchParams(searchParams);
    newParams.delete("action");
    setSearchParams(newParams, { replace: true });
    setActionHandled(true);
  }, [
    billingData,
    actionHandled,
    loadingBillingInfo,
    searchParams,
    setSearchParams,
  ]);

  /**
   * Manage billing actions (upgrade, cancel).
   */
  const manageBilling = async (
    type?: "subscription_cancel" | "subscription_update"
  ) => {
    if (type === "subscription_cancel") {
      if (billingData?.currentPlan?.canCancelSubscription === true) {
        setCancelModalOpened(true);
      }
      return;
    }

    setLoadingBilling(true);
    const currentUrl =
      window.location.origin + window.location.pathname + `?tab=Billing`;

    try {
      const response = await getBillingInformation(currentUrl, type);

      if (
        type === "subscription_update" &&
        response?.customerSessionClientSecret
      ) {
        setPricingTableData({
          "customer-session-client-secret":
            response.customerSessionClientSecret,
          "pricing-table-id": response.pricingTableId || "",
          "publishable-key": response.publishableKey || "",
        });
        setPricingModalOpened(true);
        setLoadingBilling(false);
        return;
      }

      if (response?.url) {
        window.location.href = response.url;
      } else {
        setLoadingBilling(false);
        alert(
          "Unable to retrieve billing information. Please try again later."
        );
      }
    } catch (error) {
      console.error("Error managing billing:", error);
      setLoadingBilling(false);
      const responseData = (
        error as {
          response?: {
            data?: { message?: string; content?: { error?: string } };
          };
        }
      )?.response?.data;
      const message = responseData?.message ?? extractApiError(error).message;
      const errorCode = responseData?.content?.error;
      const displayMessage = errorCode
        ? `${message} Reference: ${errorCode}`
        : message;
      showError(displayMessage, "Billing unavailable");
    }
  };

  /**
   * Handle subscription cancellation success.
   * For immediate cancellations, redirects to subscription-expired page.
   * For end-of-period cancellations, just refreshes billing data.
   */
  const handleCancelSuccess = async (result: CancelSubscriptionResponse) => {
    setCancelModalOpened(false);

    if (result.cancelledImmediately) {
      // For immediate cancellations, redirect to subscription expired page
      window.location.href = "/subscription-expired";
      return;
    }

    // For end-of-period cancellations, user still has access
    // Just refresh billing info and invalidate queries
    await loadBillingInformation();
    // Invalidate balance query to update header credits
    queryClient.invalidateQueries({ queryKey: ["balance"] });
    // Invalidate transaction history to show any refund transactions
    queryClient.invalidateQueries({ queryKey: ["balanceHistory"] });
  };

  const handlePurchaseCreditsSuccess = async () => {
    setPurchaseCreditsModalOpened(false);
    await loadBillingInformation();
    // Refresh balance so Header (NavCredits) and other consumers show updated credits
    await queryClient.refetchQueries({ queryKey: ["balance"] });
    queryClient.invalidateQueries({ queryKey: ["balanceHistory"] });
  };

  const handleCommercialAddOnSuccess = async () => {
    setCommercialAddOnModalOpened(false);
    await loadBillingInformation();
    await queryClient.refetchQueries({ queryKey: ["balance"] });
    queryClient.invalidateQueries({ queryKey: ["balanceHistory"] });
  };

  /**
   * Refresh bank status data from the server.
   */
  const refreshBankStatus = async () => {
    try {
      const data = await getBankStatus();
      setPrepFeesInfo(data);
    } catch (error) {
      console.error("Error refreshing bank status:", error);
    }
  };

  /**
   * Handle updating the user's EFIN for Pay by Prep Fees.
   *
   * @param efin - The new EFIN value (6 digits)
   */
  const handleUpdateEfin = async (efin: string) => {
    await updateBankStatus({ efin });
    // Refresh to get the latest data from server
    await refreshBankStatus();
  };

  /**
   * Handle updating the user's bank product for Pay by Prep Fees.
   *
   * @param bankProduct - The new bank product selection
   */
  const handleUpdateBank = async (bankProduct: string) => {
    await updateBankStatus({ bankProduct });
    // Refresh to get the latest data from server
    await refreshBankStatus();
  };

  const currentPlan = billingData?.currentPlan;

  if (hideBilling) {
    return null;
  }

  return (
    <div className="billing-container">
      <LoadingOverlay
        visible={loading || loadingBilling}
        zIndex={1000}
        overlayProps={{ radius: "sm", blur: 1, opacity: 0.75 }}
      />

      {/* Show error state if there's a billing error */}
      {billingError && !loadingBillingInfo && (
        <BillingErrorState
          errorMessage={billingError}
          onRetry={retryLoadBilling}
          isRetrying={loadingBillingInfo}
        />
      )}

      {/* Show skeleton loading state while fetching billing data */}
      {loadingBillingInfo && !billingError && <PlanCardSkeleton />}

      {/* Show actual content when data is loaded and no error */}
      {!loadingBillingInfo && !billingError && billingData && currentPlan && (
        <Stack gap="xl">
          <PlanCard
            currentPlan={currentPlan}
            onManageBilling={() => manageBilling()}
            onPurchaseCredits={() => setPurchaseCreditsModalOpened(true)}
            onPurchaseCommercialAddOns={() =>
              setCommercialAddOnModalOpened(true)
            }
          />

          {currentPlan.signupSource === "pay_by_refund" && (
            <PayByPrepFeesCard
              prepFeesInfo={prepFeesInfo}
              isLoading={loadingPrepFees}
              onUpdateEfin={handleUpdateEfin}
              onUpdateBank={handleUpdateBank}
            />
          )}

          {/* Transaction History Section */}
          <Box maw={1000}>
            <TransactionHistorySection
              balanceHistory={transactionHistory || []}
              membersList={membersList || []}
              isLoading={isHistoryLoading || isMembersLoading}
            />
          </Box>
          <Paper radius="md" p="lg" shadow="none" bg="red.0">
            <Group justify="space-between" mt={4}>
              <div>
                <Text size="md" c="red.9" fw={500}>
                  Cancel Plan
                </Text>
                <Text size="xs" c="red.9">
                  {currentPlan.canCancelSubscription !== true
                    ? (currentPlan.subscriptionCancellationRestrictionReason ??
                      "Contact support to cancel while your contract is active.")
                    : "Cancel your plan and lose access to all features."}
                </Text>
              </div>
              <Button
                variant="outline"
                onClick={() => manageBilling("subscription_cancel")}
                size="sm"
                color="red.4"
                c="red.9"
                disabled={
                  currentPlan.name.toLowerCase() === "free trial" ||
                  currentPlan.canCancelSubscription !== true
                }
              >
                Cancel Plan
              </Button>
            </Group>
          </Paper>
        </Stack>
      )}

      <CancelSubscriptionModal
        opened={cancelModalOpened}
        onClose={() => setCancelModalOpened(false)}
        onSuccess={handleCancelSuccess}
      />

      <PricingTableModal
        opened={pricingModalOpened}
        onClose={() => setPricingModalOpened(false)}
        pricingTableData={pricingTableData}
      />

      <PurchaseCreditsBundleModal
        opened={purchaseCreditsModalOpened}
        onClose={() => setPurchaseCreditsModalOpened(false)}
        onSuccess={handlePurchaseCreditsSuccess}
        commitmentDetails={billingData?.currentPlan?.billingDetails ?? null}
      />

      <PurchaseCommercialAddOnModal
        opened={commercialAddOnModalOpened}
        onClose={() => setCommercialAddOnModalOpened(false)}
        onSuccess={handleCommercialAddOnSuccess}
      />
    </div>
  );
};

export default Billing;
